import { useEffect, useRef, useState } from "react";
import { UserPlus, Trash2, KeyRound, Upload, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/constants";
import { useConcellos, refreshConcellos, ConcelloLogo, ConcelloSelect } from "@/lib/concellos";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDelete } from "@/components/ConfirmDelete";

const ROLE_DESC = {
  admin: "Todos los concellos: usuarios, administradores municipales y logos",
  admin_municipal: "Su concello: da de alta usuarios y observadores y sube el logo",
  usuario: "Crea y edita los eventos y planes de su concello",
  observador: "Solo consulta los mapas y planes de su concello",
};
const MUNICIPAL = ["usuario", "observador"];

const RoleSelect = ({ value, onChange, testid, disabled, roles }) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className="w-48 h-9" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{roles.map((k) => <SelectItem key={k} value={k}>{ROLES[k]}</SelectItem>)}</SelectContent>
  </Select>
);

// Logo del concello: sale en las hojas de servicio, en el plan en PDF y en la orden de servicio
const LogoCard = ({ ine, nombre, canPick, setIne, list }) => {
  const [v, setV] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const [local, setLocal] = useState(null); // tras subir o quitar, sin esperar a recargar la lista
  useEffect(() => setLocal(null), [ine]);
  const has = local ?? list.find((c) => c.ine === ine)?.logo;
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.put(`/concellos/${ine}/logo`, fd);
      await refreshConcellos();
      setLocal(true);
      setV(Date.now());
      toast.success("Logo guardado: ya sale en las hojas, el plan y la orden de servicio");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const remove = async () => {
    try { await api.delete(`/concellos/${ine}/logo`); await refreshConcellos(); setLocal(false); setV(Date.now()); toast.success("Logo quitado"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6 flex flex-wrap items-center gap-5" data-testid="concello-logo-card">
      <div className="w-24 h-24 rounded-lg border border-dashed border-slate-300 grid place-items-center bg-slate-50 shrink-0">
        {has ? <ConcelloLogo ine={ine} v={v} className="max-h-20 max-w-20" /> : <ImageOff className="w-6 h-6 text-slate-400" />}
      </div>
      <div className="flex-1 min-w-[220px] space-y-2">
        <div className="font-display font-semibold">Logo del Concello{nombre ? ` de ${nombre}` : ""}</div>
        <div className="text-xs text-slate-500">Sale en la cabecera de las hojas de servicio, del plan en PDF y de la orden de servicio. PNG o JPG, máximo 600 KB.</div>
        {canPick && <ConcelloSelect value={ine} onChange={setIne} list={list} testid="logo-concello-select" className="w-64" />}
      </div>
      <div className="flex gap-2">
        <input ref={input} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="logo-file-input" />
        <Button onClick={() => input.current?.click()} disabled={busy || !ine} data-testid="logo-upload-button"><Upload className="w-4 h-4 mr-1" /> {has ? "Cambiar logo" : "Subir logo"}</Button>
        {has && <Button variant="ghost" className="text-red-700" onClick={remove} data-testid="logo-remove-button">Quitar</Button>}
      </div>
    </div>
  );
};

export default function Users() {
  const { user: me } = useAuth();
  const general = me.role === "admin";
  const roles = general ? Object.keys(ROLES) : MUNICIPAL;
  const concellos = useConcellos();
  const nombre = (ine) => concellos.find((c) => c.ine === ine)?.nombre || ine || "—";
  const [users, setUsers] = useState([]);
  const [filtro, setFiltro] = useState(general ? "" : me.concello);
  const [logoIne, setLogoIne] = useState(me.concello);
  const empty = () => ({ name: "", username: "", email: "", password: "", role: "usuario", concello: me.concello });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [pw, setPw] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [del, setDel] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success("Usuario creado");
      setOpen(false);
      setForm(empty());
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const patch = async (id, body, msg) => {
    try { await api.patch(`/users/${id}`, body); toast.success(msg); load(); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const shown = users.filter((u) => !filtro || u.concello === filtro);
  const editable = (u) => u.id !== me.id && (general || MUNICIPAL.includes(u.role));

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1100px]">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-xs tracking-[0.25em] text-sky-600 mb-2">{general ? "ADMINISTRACIÓN GENERAL" : `CONCELLO DE ${nombre(me.concello).toUpperCase()}`}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Usuarios y perfiles</h1>
        </div>
        <Button onClick={() => { setForm(empty()); setOpen(true); }} className="h-11" data-testid="create-user-button"><UserPlus className="w-4 h-4 mr-1" /> Nuevo usuario</Button>
      </div>

      <LogoCard ine={logoIne} nombre={nombre(logoIne)} canPick={general} setIne={setLogoIne} list={concellos} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {Object.entries(ROLES).map(([k, v]) => (
          <div key={k} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="font-display font-semibold">{v}</div>
            <div className="text-xs text-slate-500 mt-1">{ROLE_DESC[k]}</div>
            <div className="font-mono text-[0.625rem] text-amber-600 mt-2">{shown.filter((u) => u.role === k).length} USUARIOS</div>
          </div>
        ))}
      </div>
      {general && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs text-slate-500">Ver</span>
          <ConcelloSelect value={filtro} onChange={setFiltro} list={concellos} allowAll testid="users-concello-filter" className="w-64" />
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="users-admin-table">
        {shown.length === 0 && <div className="px-5 py-6 text-sm text-slate-500">No hay usuarios en este concello.</div>}
        {shown.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-4 px-5 py-3 border-b border-slate-200" data-testid={`user-row-${u.id}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="text-sm font-medium">{u.name} {u.id === me.id && <span className="text-[0.625rem] text-sky-600 ml-1">(usted)</span>}</div>
              <div className="font-mono text-xs text-slate-500">{u.username ? `${u.username} · ` : ""}{u.email}</div>
              <div className="text-[0.6875rem] text-sky-700">Concello de {nombre(u.concello)}</div>
            </div>
            {general && u.id !== me.id && (
              <ConcelloSelect value={u.concello} onChange={(c) => c && patch(u.id, { concello: c }, "Concello actualizado")} list={concellos} testid={`user-concello-select-${u.id}`} className="w-44" />
            )}
            <RoleSelect value={u.role} roles={editable(u) ? roles : [u.role]} disabled={!editable(u)} testid={`user-role-select-${u.id}`} onChange={(r) => patch(u.id, { role: r }, "Perfil actualizado")} />
            <Button size="sm" variant="ghost" disabled={!editable(u) && u.id !== me.id} onClick={() => { setPw(u); setNewPw(""); }} data-testid={`user-reset-password-${u.id}`} title="Cambiar contraseña"><KeyRound className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" disabled={!editable(u)} className="hover:text-red-700" onClick={() => setDel(u)} data-testid={`user-delete-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200" data-testid="create-user-dialog">
          <DialogHeader><DialogTitle className="font-display">Nuevo usuario</DialogTitle><DialogDescription>Comunique al usuario su email y contraseña inicial. Verá y editará los eventos de su concello.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {[["name", "Nombre / empleo", "text"], ["username", "Nombre de usuario (opcional, para entrar)", "text"], ["email", "Correo electrónico", "email"], ["password", "Contraseña (mín. 10)", "password"]].map(([k, l, t]) => (
              <div key={k} className="space-y-1"><Label className="text-xs text-slate-500">{l}</Label>
                <Input type={t} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`new-user-${k}-input`} /></div>
            ))}
            <div className="space-y-1"><Label className="text-xs text-slate-500">Concello</Label>
              {general
                ? <ConcelloSelect value={form.concello} onChange={(c) => setForm({ ...form, concello: c })} list={concellos} testid="new-user-concello-select" className="w-full" />
                : <div className="h-9 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 text-sm">{nombre(me.concello)}</div>}
            </div>
            <div className="space-y-1"><Label className="text-xs text-slate-500">Perfil</Label><RoleSelect value={form.role} roles={roles} onChange={(r) => setForm({ ...form, role: r })} testid="new-user-role-select" /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={create} disabled={!form.concello} data-testid="new-user-submit-button">Crear usuario</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pw} onOpenChange={(o) => !o && setPw(null)}>
        <DialogContent className="bg-white border-slate-200" data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle className="font-display">Nueva contraseña · {pw?.name}</DialogTitle></DialogHeader>
          <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Mínimo 10 caracteres" data-testid="reset-password-input" />
          <DialogFooter><Button onClick={async () => { if (await patch(pw.id, { password: newPw }, "Contraseña actualizada")) setPw(null); }} data-testid="reset-password-submit">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDelete open={!!del} title={`¿Eliminar a ${del?.name}?`} onCancel={() => setDel(null)}
        onConfirm={async () => { try { await api.delete(`/users/${del.id}`); toast.success("Usuario eliminado"); setDel(null); load(); } catch (e) { toast.error(errMsg(e)); } }} />
    </div>
  );
}
